import React, { useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import PermMediaSharpIcon from '@mui/icons-material/PermMediaSharp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { TrackedButton } from './TrackedButton';
import { ComposePostEvents } from '../services/analytics/events';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

/**
 * MediaControls Component
 * Provides buttons for uploading, generating, and removing media
 * Handles file input and validation
 */
const MediaControls = ({
    hasMedia = false,
    disabled = false,
    onFileUpload,
    onGenerateMedia,
    onRemoveMedia,
    onImageGalleryOpen,
    acceptedFileTypes = "image/*, video/mp4",
    showGenerateMedia = true,
    compact = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const fileInputRef = useRef(null);
    const { canCreateMedia } = useFeatureAccess();

    const handleFileInputChange = (event) => {
        if (onFileUpload) {
            onFileUpload(event);
        }
        // Reset file input to allow selecting the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleGenerateClick = () => {
        if (onImageGalleryOpen) {
            onImageGalleryOpen();
        } else if (onGenerateMedia) {
            onGenerateMedia();
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            ...(compact && { flexDirection: 'column' })
        }}>
            {/* Generate Media Button */}
            {canCreateMedia && showGenerateMedia && (
                <TrackedButton
                    fullWidth={isMobile || compact}
                    color="primary"
                    startIcon={<PermMediaSharpIcon/>}
                    onClick={handleGenerateClick}
                    disabled={disabled}
                    trackingData={ComposePostEvents.GENERATE_MEDIA_BTN_CLICKED}
                    size={compact ? "small" : "medium"}
                >
                    {hasMedia ? 'Edit Media' : 'Create Media'}
                </TrackedButton>
            )}

            {/* Upload Media Button */}
            {!hasMedia && (
                <TrackedButton
                    fullWidth={isMobile || compact}
                    disabled={disabled || hasMedia}
                    color="primary"
                    startIcon={<UploadFileIcon/>}
                    onClick={() => fileInputRef.current?.click()}
                    trackingData={ComposePostEvents.UPLOAD_MEDIA_BTN_CLICKED}
                    size={compact ? "small" : "medium"}
                >
                    Upload Media
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptedFileTypes}
                        style={{display: 'none'}}
                        onChange={handleFileInputChange}
                    />
                </TrackedButton>
            )}

            {/* Remove Media Button */}
            {hasMedia && onRemoveMedia && (
                <TrackedButton
                    fullWidth={isMobile || compact}
                    color="error"
                    startIcon={<DeleteIcon/>}
                    onClick={onRemoveMedia}
                    disabled={disabled}
                    trackingData={ComposePostEvents.REMOVE_MEDIA_BTN_CLICKED}
                    size={compact ? "small" : "medium"}
                >
                    Remove Media
                </TrackedButton>
            )}
        </Box>
    );
};

export default MediaControls; 